import { useCallback } from 'react';
import { useSetAtom } from 'jotai';
import { Number3, TransformMode } from '@fabric-web/3d-viewer';
import { CuboidClickMode } from '@/types';
import { CuboidBase, CuboidDataHandlers, CuboidState, CuboidStyleHandlers } from '../types';
import { useMakeStyledCuboid } from './useMakeStyledCuboid';
import { generateMongoObjectId } from '@/utils/databaseHelpers';
import { DEFAULT_CUBOID_ROTATION, DEFAULT_CUBOID_SCALE } from '../constants';

export const useAddCuboid = <CuboidT extends CuboidBase>(
  cuboidState: CuboidState<CuboidT>,
  dataHandlers: CuboidDataHandlers<CuboidT>,
  styleHandlers: CuboidStyleHandlers<CuboidT>
) => {
  const setCurrentCuboid = useSetAtom(cuboidState.currentCuboid);
  const setIsCuboidEditorEnabled = useSetAtom(cuboidState.isCuboidEditorEnabled);
  const setCuboidTransformMode = useSetAtom(cuboidState.cuboidTransformMode);
  const setCuboidClickMode = useSetAtom(cuboidState.cuboidClickMode);

  const makeStyledCuboid = useMakeStyledCuboid(styleHandlers);

  const addCuboid = useCallback(
    (partialCuboid: Partial<CuboidBase> & { position: Number3 }) => {
      const cuboid = {
        id: partialCuboid.id ?? generateMongoObjectId(),
        rotation: partialCuboid.rotation ?? DEFAULT_CUBOID_ROTATION,
        scale: partialCuboid.scale ?? DEFAULT_CUBOID_SCALE,
        ...partialCuboid,
      };
      const newCuboid = dataHandlers.createCuboid(cuboid);
      if (!newCuboid) {
        console.error('Unable to create cuboid');
        return;
      }
      const styledCuboid = makeStyledCuboid(newCuboid);
      // Enable edit mode for the new cuboid
      setCurrentCuboid(styledCuboid);
      setCuboidTransformMode(TransformMode.BoxResize);
      setIsCuboidEditorEnabled(true);
      setCuboidClickMode(CuboidClickMode.Default);
    },
    [
      dataHandlers,
      makeStyledCuboid,
      setCurrentCuboid,
      setCuboidTransformMode,
      setIsCuboidEditorEnabled,
      setCuboidClickMode,
    ]
  );

  return addCuboid;
};
